/**
 * UserManagementPage — `/security/users`.
 *
 * Lists users from service-identity (`GET /v1/identity/users`), lets the
 * operator enable/disable accounts, navigate to individual profiles, and
 * create new users via the admin POST endpoint.
 *
 * Wired to real endpoints via `apps/web-staff/src/lib/api/identity-admin.ts`.
 * Requires `Identity/readUser` on the caller's JWT — granted to
 * dev-superadmin-role via 20260522_b3_security_admin_perms.sql. Create flow
 * additionally requires `Identity/createUser` (granted via
 * 20260509_security_admin_full_perms.sql).
 */
import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, UserCheck, UserPlus, UserX, Users, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type UserDto,
  createUser,
  disableUser,
  enableUser,
  listUsers,
} from '../../lib/api/identity-admin';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

function UserRow({
  user,
  onToggle,
  toggling,
}: {
  user: UserDto;
  onToggle: (u: UserDto) => void;
  toggling: boolean;
}): JSX.Element {
  const isActive = user.status === 'ACTIVE';
  return (
    <tr data-testid={`user-row-${user.id}`} className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
          <p className="text-xs text-muted font-mono">{user.email}</p>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-muted font-mono">{user.id.slice(0, 8)}…</td>
      <td className="px-4 py-3">
        <StatusBadge
          status={
            user.status === 'ACTIVE'
              ? 'ACTIVE'
              : user.status === 'DISABLED'
                ? 'DISABLED'
                : user.status === 'LOCKED'
                  ? 'LOCKED'
                  : 'PENDING'
          }
        />
      </td>
      <td className="px-4 py-3 text-xs text-muted">
        {user.isPlatformAdmin ? (
          <span className="px-2 py-0.5 bg-nipost-green-light text-nipost-green text-[10px] rounded font-semibold">
            Platform admin
          </span>
        ) : (
          <span className="text-muted">Op {user.operatorId}</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted">
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('en-GB') : '—'}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid={isActive ? `disable-user-${user.id}` : `enable-user-${user.id}`}
            onClick={() => onToggle(user)}
            disabled={toggling}
            className={`px-2 py-1 text-xs font-semibold rounded border disabled:opacity-50 ${
              isActive
                ? 'border-red-300 text-red-700 hover:bg-red-50'
                : 'border-nipost-green text-nipost-green hover:bg-nipost-green-light'
            }`}
          >
            {toggling ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : isActive ? (
              <>
                <UserX className="w-3 h-3 inline mr-1" />
                Disable
              </>
            ) : (
              <>
                <UserCheck className="w-3 h-3 inline mr-1" />
                Enable
              </>
            )}
          </button>
          <Link
            to={`/security/users/${user.id}`}
            data-testid={`view-user-${user.id}`}
            className="px-2 py-1 text-xs font-semibold text-nipost-green border border-nipost-green rounded hover:bg-nipost-green-light"
          >
            View
          </Link>
        </div>
      </td>
    </tr>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [operatorId, setOperatorId] = useState(0);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      createUser({
        email: email.trim(),
        displayName: displayName.trim(),
        phone: phone.trim() || undefined,
        password: password.trim() || undefined,
        operatorId,
        isPlatformAdmin,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  return (
    <div
      data-testid="create-user-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">New user</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted hover:text-gray-900"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-xs font-medium text-muted block mb-1">Email *</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="new-user-email"
              className="w-full px-3 py-2 border border-border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted block mb-1">Display name *</span>
            <input
              type="text"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="new-user-display-name"
              className="w-full px-3 py-2 border border-border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted block mb-1">Phone (E.164)</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="new-user-phone"
              placeholder="+234…"
              className="w-full px-3 py-2 border border-border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted block mb-1">
              Initial password (optional — leave blank for SSO/invitation flow)
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="new-user-password"
              className="w-full px-3 py-2 border border-border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted block mb-1">Operator ID *</span>
            <input
              type="number"
              required
              min={0}
              value={operatorId}
              onChange={(e) => setOperatorId(Math.max(0, Number(e.target.value || 0)))}
              data-testid="new-user-operator-id"
              className="w-full px-3 py-2 border border-border rounded-lg"
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isPlatformAdmin}
              onChange={(e) => setIsPlatformAdmin(e.target.checked)}
              data-testid="new-user-platform-admin"
            />
            <span className="text-xs">Platform admin (bypasses RBAC matrix — use sparingly)</span>
          </label>
        </div>

        {create.isError ? (
          <p className="text-sm text-danger" data-testid="new-user-error">
            {(create.error as Error).message}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-muted border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={
              create.isPending || email.trim().length === 0 || displayName.trim().length === 0
            }
            data-testid="new-user-submit"
            className="px-3 py-2 text-sm font-semibold text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UserManagementPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const users = useQuery({
    queryKey: ['identity', 'users'],
    queryFn: listUsers,
    retry: false,
  });

  const toggle = useMutation({
    mutationFn: (user: UserDto) =>
      user.status === 'ACTIVE' ? disableUser(user.id) : enableUser(user.id),
    onMutate: (user) => setTogglingId(user.id),
    onSettled: () => {
      setTogglingId(null);
      void queryClient.invalidateQueries({ queryKey: ['identity', 'users'] });
    },
  });

  const denied =
    users.isError &&
    users.error instanceof ApiError &&
    (users.error.status === 401 || users.error.status === 403);

  const filtered = (users.data ?? []).filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      u.displayName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.id.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="User management"
        description="Platform and operator users."
        actions={
          <button
            type="button"
            data-testid="open-new-user"
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark"
          >
            <UserPlus className="w-4 h-4" />
            New user
          </button>
        }
      />

      {denied ? (
        <div
          data-testid="permission-required"
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800"
        >
          <code className="font-mono">Identity/readUser</code> permission required.
          Grant it to the caller&apos;s role via the identity admin API or
          re-run the B3 migration seed.
        </div>
      ) : users.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          Identity service temporarily unavailable: {(users.error as Error).message}
        </div>
      ) : null}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by name, email or id…"
      />

      {toggle.isError ? (
        <p className="text-sm text-danger">{(toggle.error as Error).message}</p>
      ) : null}

      <div
        data-testid="user-list"
        className="bg-white border border-border rounded-xl overflow-hidden"
      >
        {users.isPending ? (
          <p className="px-4 py-8 text-center text-sm text-muted">Loading users…</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">User</th>
                <th className="text-left px-4 py-2">Id</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Role</th>
                <th className="text-left px-4 py-2">Last login</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">
                    {search ? 'No users match the filter.' : 'No users found.'}
                  </td>
                </tr>
              ) : (
                filtered.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    onToggle={(user) => toggle.mutate(user)}
                    toggling={togglingId === u.id}
                  />
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted">
        <Users className="w-3 h-3" />
        {users.data ? `${users.data.length} user(s) total` : '—'}
      </div>

      {createOpen ? (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ['identity', 'users'] });
          }}
        />
      ) : null}
    </div>
  );
}
